// InactiveChart.js
import React, { useEffect, useRef, useState, useMemo } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../../../../utils/SamuiUtils';

const useChartData = ({ data, sortBy, topCount }) => {
  return useMemo(() => {
    const sorted = [...data]
      .sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0))
      .slice(0, topCount);

    const labels = sorted.map(item =>
      item.AR_Name.length > 20
        ? item.AR_Name.slice(0, 20) + '...'
        : item.AR_Name
    );

    const values = {
      purchaseCount: sorted.map(i => i.purchaseCount),
      daysSinceLast:  sorted.map(i => i.daysSinceLast),
      totalSpent:     sorted.map(i => i.totalSpent),
    };

    const dataset = [];
    const common = { borderWidth: 2, borderRadius: 2, borderSkipped: false };
    switch (sortBy) {
      case 'purchaseCount':
        dataset.push({ label: 'จำนวนคำสั่งซื้อ', data: values.purchaseCount, backgroundColor: '#0d6efd', borderColor: '#0d6efd', ...common });
        break;
      case 'daysSinceLast':
        dataset.push({ label: 'วันนับจากการสั่งซื้อล่าสุด', data: values.daysSinceLast, backgroundColor: '#ffc107', borderColor: '#ffc107', ...common });
        break;
      case 'totalSpent':
        dataset.push({ label: 'ยอดใช้จ่ายรวม (฿)', data: values.totalSpent, backgroundColor: '#28a745', borderColor: '#28a745', ...common });
        break;
      default:
        dataset.push({ label: 'จำนวนคำสั่งซื้อ', data: values.purchaseCount, backgroundColor: '#0d6efd', borderColor: '#0d6efd', ...common });
    }

    return { labels, dataset, raw: sorted };
  }, [data, sortBy, topCount]);
};

const useChartConfig = ({ labels, dataset, raw, sortBy }) => {
  return useMemo(() => ({
    type: 'bar',
    data: { labels, datasets: dataset },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `ลูกค้าที่ไม่เคลื่อนไหว Top ${labels.length}`,
          font: { size: 18, weight: 'bold' }
        },
        tooltip: {
          callbacks: {
            label: ctx => {
              const item = raw[ctx.dataIndex];
              return `${ctx.dataset.label}: ${ctx.formattedValue}`;
            }
          }
        }
      },
      scales: {
        x: { ticks: { maxRotation: 45, minRotation: 0 } },
        y: { beginAtZero: true }
      }
    }
  }), [labels, dataset, raw, sortBy]);
};

export default function InactiveChart({ data }) {
  const [sortBy, setSortBy] = useState('purchaseCount');
  const [topCount, setTopCount] = useState(10);
  const chartRef = useRef(null);
  const { labels, dataset, raw } = useChartData({ data, sortBy, topCount });
  const config = useChartConfig({ labels, dataset, raw, sortBy });

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = new Chart(chartRef.current.getContext('2d'), config);
    return () => chart.destroy();
  }, [config]);

  if (!data.length) {
    return <div className="text-center text-muted p-5">ไม่พบข้อมูลสำหรับกราฟ</div>;
  }

  return (
    <div>
      <div className="d-flex mb-3 gap-2">
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="form-select form-select-sm">
          <option value="purchaseCount">จำนวนคำสั่งซื้อ</option>
          <option value="daysSinceLast">วันนับจากล่าสุด</option>
          <option value="totalSpent">ยอดใช้จ่ายรวม</option>
        </select>
        <select value={topCount} onChange={e => setTopCount(+e.target.value)} className="form-select form-select-sm">
          {[5,10,15,20].map(n => <option key={n} value={n}>Top {n}</option>)}
        </select>
      </div>
      <div style={{ height: 400 }}>
        <canvas ref={chartRef} />
      </div>
    </div>
  );
}
